import { HistoryItem } from './MatchmakingHistoryPopover';

interface MatchmakingHistoryDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  historyItem: HistoryItem | null;
}

const STEP_QUESTIONS = [
  "Tell us about the situation you are addressing and who is involved. What are you searching for and and how can bQuest help?",
  "Are there any specifics that you think are important? The more detail you provide the more accurate our matchmaking will be!",
  "What matters most to you in your search? Think about things like urgency, budget, or location for example"
];

const MatchmakingHistoryDetailsModal = ({
  isOpen,
  onClose,
  historyItem,
}: MatchmakingHistoryDetailsModalProps) => {
  if (!isOpen || !historyItem) return null;

  const answers = historyItem.answers;
  const filters = historyItem.filters;
  const clientContext = historyItem.client_context;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 backdrop-blur-sm px-2 sm:px-4"
      style={{ fontFamily: '"Inter", sans-serif' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative w-full max-w-[64rem] rounded-[15px] bg-[#F9FAFB] shadow-[0_30px_80px_rgba(15,23,42,0.18)] max-h-[90vh] overflow-hidden flex flex-col">
        <style>{`
          .matchmaking-history-modal-scrollbar::-webkit-scrollbar {
            width: 10px;
          }
          .matchmaking-history-modal-scrollbar::-webkit-scrollbar-track {
            background: #F9FAFB;
            border-radius: 10px;
          }
          .matchmaking-history-modal-scrollbar::-webkit-scrollbar-thumb {
            background: #026857;
            border-radius: 10px;
          }
          .matchmaking-history-modal-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #025247;
          }
        `}</style>
        <div className="relative overflow-y-auto flex-1 px-4 py-6 sm:px-6 sm:py-8 md:px-10 md:py-10 matchmaking-history-modal-scrollbar" style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#026857 #E5E7EB'
        }}>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 sm:right-6 sm:top-6 text-[#9AA3B7] transition-colors hover:text-[#6f778c] z-50"
          aria-label="Close modal"
        >
          <svg
            width="25"
            height="25"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 5l10 10M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="mb-2 sm:mb-2">
          <h2 className="text-xl sm:text-2xl font-semibold text-[#111827] mb-1">
            Search Details
          </h2>
          <p className="text-xs sm:text-sm font-termina text-[#6B7280]">
            View the questions and location details for this search.
          </p>
        </div>

        {/* Content */}
        <div>
          {/* Search Prompt with Questions and Answers */}
          <div className="bg-white rounded-lg p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-termina font-semibold text-[#111827] mb-4">
              Search Prompt
            </h3>
            <div className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm font-termina text-[#111827] mb-6">
              {historyItem.promptText || '-'}
            </div>

            {/* Questions and Answers */}
            {answers && (
              <div className="space-y-2">
                {/* Question 1 */}
                <div>
                  <h4 className="text-sm font-termina font-semibold text-[#111827] mb-1.5">
                    {STEP_QUESTIONS[0]}
                  </h4>
                  <div className="w-full min-h-[100px] rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm font-termina text-[#111827] whitespace-pre-wrap">
                    {answers.answer_q1 || '-'}
                  </div>
                </div>

                {/* Question 2 */}
                <div>
                  <h4 className="text-sm font-termina font-semibold text-[#111827] mb-1.5">
                    {STEP_QUESTIONS[1]}
                  </h4>
                  <div className="w-full min-h-[100px] rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm font-termina text-[#111827] whitespace-pre-wrap">
                    {answers.answer_q2 || '-'}
                  </div>
                </div>

                {/* Question 3 */}
                <div>
                  <h4 className="text-sm font-termina font-semibold text-[#111827] mb-1.5">
                    {STEP_QUESTIONS[2]}
                  </h4>
                  <div className="w-full min-h-[100px] rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm font-termina text-[#111827] whitespace-pre-wrap">
                    {answers.answer_q3 || '-'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Location Details */}
          {filters && (
            <div className="bg-white rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-termina font-semibold text-[#111827] mb-4">
                Location Details
              </h3>
              
              {/* Address */}
              <div className="mb-4">
                <label className="mb-2 block text-sm font-termina font-semibold text-[#3E424F]">
                  Address
                </label>
                <div className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm font-termina text-[#111827]">
                  {filters.address || '-'}
                </div>
              </div>

              {/* Location Tags */}
              {(filters.state || filters.county || filters.zipCode) && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {filters.state && (
                    <span className="rounded-full px-4 py-2 text-sm font-termina bg-[#026857]/10 text-[#026857] border border-[#026857]">
                      State: {filters.state}
                    </span>
                  )}
                  {filters.county && (
                    <span className="rounded-full px-4 py-2 text-sm font-termina bg-[#026857]/10 text-[#026857] border border-[#026857]">
                      County: {filters.county}
                    </span>
                  )}
                  {filters.zipCode && (
                    <span className="rounded-full px-4 py-2 text-sm font-termina bg-[#026857]/10 text-[#026857] border border-[#026857]">
                      ZIP: {filters.zipCode}
                    </span>
                  )}
                </div>
              )}

              {/* Service Types */}
              <div className="mb-4">
                <label className="mb-3 block text-sm font-termina font-semibold text-[#3E424F]">
                  Service Types
                </label>
                <div className="flex flex-wrap gap-3">
                  {filters.serviceRange && filters.serviceRange.length > 0 ? (
                    filters.serviceRange.map((option) => (
                      <div
                        key={option}
                        className="group relative overflow-hidden rounded-full border px-4 py-2 text-md font-termina bg-gradient-to-r from-[#026857]/10 via-white to-[#026857]/10 text-[#026857] border-[#026857]"
                      >
                        <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-[14px] font-bold bg-[#026857] text-white">
                          ✓
                        </span>
                        <span className="relative z-10">{option}</span>
                      </div>
                    ))
                  ) : (
                    <span className="text-sm font-termina text-[#6B7280]">-</span>
                  )}
                </div>
              </div>

              {/* Search Radius */}
              {filters.distanceMiles && (
                <div>
                  <label className="mb-2 block text-sm font-termina font-semibold text-[#3E424F]">
                    Search Radius
                  </label>
                  <div className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm font-termina text-[#111827]">
                    {filters.distanceMiles} Miles
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Client Context (if available) */}
          {clientContext && (clientContext.relationship || clientContext.age_range || clientContext.reason) && (
            <div className="bg-white rounded-lg p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-termina font-semibold text-[#111827] mb-4">
                Client Context
              </h3>
              <div className="space-y-3">
                {clientContext.relationship && (
                  <div>
                    <label className="block text-sm font-termina font-medium text-[#3E424F] mb-1">
                      Relationship
                    </label>
                    <div className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-2 text-sm font-termina text-[#111827]">
                      {clientContext.relationship}
                    </div>
                  </div>
                )}
                {clientContext.age_range && (
                  <div>
                    <label className="block text-sm font-termina font-medium text-[#3E424F] mb-1">
                      Age Range
                    </label>
                    <div className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-2 text-sm font-termina text-[#111827]">
                      {clientContext.age_range}
                    </div>
                  </div>
                )}
                {clientContext.reason && (
                  <div>
                    <label className="block text-sm font-termina font-medium text-[#3E424F] mb-1">
                      Reason
                    </label>
                    <div className="w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-2 text-sm font-termina text-[#111827]">
                      {clientContext.reason}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center gap-2 rounded-lg bg-[#026857] px-4 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-termina font-medium text-white transition-colors hover:bg-[#025247]"
          >
            Close
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default MatchmakingHistoryDetailsModal;

